import { useEffect, useRef, useState } from 'react';
import styles from './TextBlock.module.css';

/**
 * Authored prose — the analyst's own words on the canvas.
 *
 * Every other block describes where to fetch its figures. This one carries its
 * content directly, which is the single deliberate exception to that rule: a
 * sentence a person wrote cannot misstate a number, because it contains no
 * fetched number. The invariant that matters — no *figure* on the canvas was
 * authored rather than queried — is untouched.
 *
 * It exists because a deliverable is not a dashboard. The reports this product
 * is meant to accelerate are narrative-led: the reasoning is the product, and
 * the charts are evidence for it. Without a way to write the reasoning down,
 * the canvas can only ever produce the evidence.
 *
 * Formatting is deliberately tiny — headings, bullets, bold. Enough for a
 * summary or a caveat, not enough to reinvent a word processor inside a block.
 */

export type TextVariant = 'body' | 'heading' | 'callout';

/** `**bold**` → <strong>. The only inline mark worth supporting here. */
function inline(text: string, keyPrefix: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );
}

/** Blank lines separate paragraphs; `- ` lines group into a list. */
export function renderProse(content: string) {
  const lines = content.split('\n');
  const out: React.ReactNode[] = [];
  let bullets: string[] = [];
  let para: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} className={styles.list}>
        {bullets.map((b, i) => (
          <li key={i}>{inline(b, `li-${out.length}-${i}`)}</li>
        ))}
      </ul>
    );
    bullets = [];
  };

  const flushPara = () => {
    if (para.length === 0) return;
    const text = para.join(' ');
    out.push(
      <p key={`p-${out.length}`} className={styles.para}>
        {inline(text, `p-${out.length}`)}
      </p>
    );
    para = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ')) {
      flushPara();
      bullets.push(trimmed.slice(2));
    } else if (trimmed === '') {
      flushPara();
      flushBullets();
    } else {
      flushBullets();
      para.push(trimmed);
    }
  }
  flushPara();
  flushBullets();
  return out;
}

interface Props {
  content: string;
  variant: TextVariant;
  /** Absent when read-only — export and print pass no handler. */
  onChange?: (next: string) => void;
  placeholder?: string;
}

export function TextBlock({ content, variant, onChange, placeholder }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  /* Seed the draft on the way into edit mode rather than syncing it from a
     prop change. Syncing needed an effect whose whole body was a setState,
     which cascades a second render on every keystroke of the parent — and it
     also meant a `content` change arriving mid-edit silently overwrote what
     was being typed. Both entry points seed, so the draft is always fresh. */
  const startEditing = () => {
    setDraft(content);
    setEditing(true);
  };

  useEffect(() => {
    const el = areaRef.current;
    if (!editing || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  /* Grow with the content so the whole passage stays visible while writing —
     a fixed-height box that scrolls hides the paragraph you are editing. */
  useEffect(() => {
    const el = areaRef.current;
    if (!editing || !el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== content) onChange?.(draft);
  };

  if (editing && onChange) {
    return (
      <textarea
        ref={areaRef}
        className={`${styles.editor} ${styles[variant]}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          /* Enter breaks the line — this is prose, not a chat box. Escape
             abandons the edit; Cmd/Ctrl+Enter commits without reaching for
             the mouse. */
          if (e.key === 'Escape') {
            setDraft(content);
            setEditing(false);
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            commit();
          }
        }}
        placeholder={placeholder}
      />
    );
  }

  const empty = content.trim() === '';

  if (empty) {
    return onChange ? (
      <button type="button" className={styles.empty} onClick={startEditing}>
        {placeholder ?? 'Click to write…'}
      </button>
    ) : null;
  }

  return (
    <div
      className={`${styles.prose} ${styles[variant]} ${onChange ? styles.editable : ''}`}
      onDoubleClick={onChange ? startEditing : undefined}
      /* Double-click to edit keeps single-click free for text selection, which
         is what you want far more often on a paragraph. */
      title={onChange ? 'Double-click to edit' : undefined}
    >
      {variant === 'heading' ? (
        <h2 className={styles.headingText}>{inline(content.trim(), 'h')}</h2>
      ) : (
        renderProse(content)
      )}
    </div>
  );
}
